/**
 * The draft screen, shared by single-player and online. Reads everything from a
 * DraftBackend (the local runner or the online session), so the UI is identical
 * in both. Shows the shared pool as pickable card tiles (each with its teaching
 * tier), a single "on deck" preview of the next card to enter the pool, each
 * side's drafted deck with a live cost-curve coach and remaining budget, whose
 * pick it is, and an in-column "done" button to finish early. Read-only for
 * spectators.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import {
  canTake,
  cardTypeCost,
  deckShape,
  type CardTypeId,
  type DeckBuild,
  type PlayerId,
} from "../../core";
import type { DraftBackend } from "../../app/draftRunner";
import { PLAYER_ACCENT } from "../../app/config";
import { CardFace, cardEntry } from "../components/CardFace";
import { ThemeToggle } from "../components/ThemeToggle";
import { RulesOverlay } from "../components/RulesOverlay";
import { Icon } from "../components/Icon";

export function DraftScreen(props: {
  backend: DraftBackend;
  /** Display names per seat. */
  names: readonly [string, string];
  onExit: () => void;
  spectator?: boolean;
}) {
  const b = props.backend;
  const state = () => b.state;
  const me = (): PlayerId | null => (props.spectator ? null : b.mySeat);
  const myTurn = () => me() !== null && state().turn === me() && state().phase === "drafting";
  const done = () => state().phase === "done";
  const [showRules, setShowRules] = createSignal(false);

  const seatLabel = (seat: PlayerId) =>
    seat === me() ? "You" : props.names[seat] || `Player ${seat + 1}`;

  const status = () => {
    if (done()) return "Draft complete — here's your army…";
    if (me() === null) return `${seatLabel(state().turn)} is picking…`;
    if (myTurn()) return "Your pick";
    if (b.thinking?.()) return "Computer is picking…";
    return `${seatLabel(state().turn)} is picking…`;
  };

  // A brief "they took X" flash whenever a pick lands — makes the shared-pool
  // denial duel legible without a permanent readout.
  const [flash, setFlash] = createSignal<string | null>(null);
  let flashTimer: ReturnType<typeof setTimeout> | undefined;
  createEffect(() => {
    const lp = state().lastPick;
    const card = lp?.card;
    const seat = lp?.seat;
    if (lp == null || card === undefined || seat === undefined) return;
    const who = seat === me() ? "You" : seatLabel(seat);
    setFlash(`${who} took ${cardEntry(card).name}`);
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => setFlash(null), 1800);
  });
  onCleanup(() => clearTimeout(flashTimer));

  return (
    <div class="app app--wide">
      <div class="topbar">
        <div class="brand">
          <h1>Chessdeck</h1>
          <span class="tag">draft</span>
        </div>
        <div class="topbar-actions">
          <button class="icon-link" title="How to play" onClick={() => setShowRules(true)}>
            <Icon name="help" />
          </button>
          <ThemeToggle />
          <button class="btn btn-ghost" onClick={props.onExit}>
            {props.spectator ? "Leave" : "Quit"}
          </button>
        </div>
      </div>

      <div class="draft" classList={{ "draft-done": done() }}>
        <div class="turn-banner" classList={{ mine: myTurn() }}>
          <span class="tb-status" style={{ color: PLAYER_ACCENT[state().turn] }}>
            {status()}
          </span>
          <span class="tb-last">
            Pick cards to build your deck — you draft until your points run out. Both armies are
            chosen from the same pool, so take what you want before your opponent does.
          </span>
        </div>

        <div class="draft-main">
          <DeckColumn seat={0} backend={b} label={seatLabel(0)} mine={me() === 0} />

          <div
            class="draft-pool"
            classList={{ mine: myTurn() }}
            style={{ "--turn-accent": PLAYER_ACCENT[state().turn] }}
          >
            <div class="draft-pool-head">
              <h2>Shared pool</h2>
              <Show when={flash()}>
                {(msg) => <span class="pool-flash">{msg()}</span>}
              </Show>
            </div>
            <div class="pool-grid">
              <For each={state().pool}>
                {(card, i) => {
                  const affordable = () => me() !== null && canTake(state(), me()!, card);
                  const pickable = () => myTurn() && affordable();
                  return (
                    <button
                      class="pool-card"
                      classList={{ pickable: pickable(), locked: !pickable() }}
                      style={{ "--reveal-i": i() }}
                      disabled={!pickable()}
                      title={
                        myTurn() && !affordable()
                          ? "You can't afford that, or you're at its limit"
                          : undefined
                      }
                      onClick={() => b.pick(card)}
                    >
                      <CardFace id={card} owner={me() ?? state().turn} />
                    </button>
                  );
                }}
              </For>
            </div>

            <Show when={!done() && state().nextUp}>
              {(card) => (
                <div class="draft-ondeck">
                  <span class="ondeck-label">Next in the pool →</span>
                  <div class="ondeck-tile">
                    <CardFace id={card()} owner={state().turn} showMove={false} />
                  </div>
                </div>
              )}
            </Show>
          </div>

          <DeckColumn seat={1} backend={b} label={seatLabel(1)} mine={me() === 1} />
        </div>
      </div>

      <Show when={showRules()}>
        <RulesOverlay onClose={() => setShowRules(false)} />
      </Show>
    </div>
  );
}

/** One side's drafted deck so far, with a cost-curve coach and remaining points. */
function DeckColumn(props: {
  seat: PlayerId;
  backend: DraftBackend;
  label: string;
  mine: boolean;
}) {
  const state = () => props.backend.state;
  const deck = (): DeckBuild => state().decks[props.seat];
  // Card types in the deck, most-expensive first, with counts.
  const rows = createMemo(() =>
    (Object.entries(deck()) as [CardTypeId, number][])
      .filter(([, n]) => n > 0)
      .sort((a, b) => cardTypeCost(b[0]) - cardTypeCost(a[0])),
  );
  const cards = () => rows().reduce((s, [, n]) => s + n, 0);
  const active = () => state().turn === props.seat && state().phase === "drafting";
  const canPass = () => props.mine && active();

  return (
    <div class="panel draft-deck" classList={{ mine: props.mine, active: active() }}>
      <div class="draft-deck-head">
        <span class="swatch" style={{ background: PLAYER_ACCENT[props.seat] }} />
        <strong>{props.label}</strong>
        <span class="draft-budget">{state().budgets[props.seat]} pts left</span>
      </div>
      <div class="draft-deck-list">
        <Show when={rows().length > 0} fallback={<p class="budget-sub">No cards yet.</p>}>
          <For each={rows()}>
            {([card, n]) => (
              <div class="draft-deck-row">
                <span class="dd-count">{n}×</span>
                <span class="dd-name">{cardEntry(card).name}</span>
                <span class="dd-cost">{cardTypeCost(card) * n}</span>
              </div>
            )}
          </For>
        </Show>
      </div>

      <Show when={cards() > 0}>
        <CurveCoach seat={props.seat} backend={props.backend} />
      </Show>
      <div class="budget-sub">{cards()} cards</div>

      <Show when={canPass()}>
        <button class="btn btn-ghost draft-pass" onClick={() => props.backend.pass()}>
          I'm done drafting
        </button>
      </Show>
    </div>
  );
}

/** A tiny cost-curve histogram (cheap / mid / heavy) plus one plain-language tip. */
function CurveCoach(props: { seat: PlayerId; backend: DraftBackend }) {
  const shape = createMemo(() => deckShape(props.backend.state.decks[props.seat]));
  const bands: readonly { key: string; label: string }[] = [
    { key: "cheap", label: "1–2" },
    { key: "mid", label: "3–5" },
    { key: "heavy", label: "6+" },
  ];
  const peak = () => Math.max(1, ...shape().tiers);
  return (
    <div class="curve-coach">
      <div class="curve-bars">
        <For each={bands}>
          {(band, i) => {
            const n = () => shape().tiers[i()]!;
            return (
              <div class="curve-band">
                <div class="curve-track">
                  <div
                    class="curve-fill"
                    style={{ height: `${Math.round((n() / peak()) * 100)}%` }}
                  />
                </div>
                <span class="curve-n">{n()}</span>
                <span class="curve-label">{band.label}</span>
              </div>
            );
          }}
        </For>
      </div>
      <p class={`curve-tip curve-tip-${shape().tip.kind}`}>{shape().tip.text}</p>
    </div>
  );
}
